'use client'

import { ID } from '@/core/types/models/id.model'
import { AirportModel } from '@/features/locations/types/models/airport.model'
import { useCountries } from '@/features/locations/ui'
import { useAirports } from '@/features/locations/ui/hooks/use-airports'
import {
  useSearchFromLocation,
  useSearchToLocation,
} from '@/features/locations/ui/hooks/use-search-location'
import { useReservationStore } from '@/features/reservation/store'
import { BookFormModel } from '@/features/reservation/types/models/book-form.model'
import { useCallback, useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import {
  buildAirportFromInitial,
  mergeAirportOptions,
  useDebouncedSearch,
} from './helpers'

import { inputOutlined } from '@/core/constants/class-names'
import { cn } from '@/core/lib/utils/cn'
import { Modal, ModalBody } from '@/core/ui/atoms/modal'
import { Combobox } from '@/core/ui/forms/combobox'
import { Divider } from '@heroui/divider'
import { useTranslations } from 'next-intl'
import { useState } from 'react'
import { Controller } from 'react-hook-form'
import { Separator } from '../location-separator'
import { PickOnMap } from '../pick-on-map'
import { CountryOption } from './country-option'
import { LocationInput } from './location-input'

type Props = { initialValues?: BookFormModel }

export const TripLocationInput: React.FC<Props> = ({ initialValues }) => {
  const t = useTranslations()
  const [pickOnMap, setPickOnMap] = useState<'from' | 'to' | null>(null)
  const { control, setValue, watch } = useFormContext()
  const selectedLocations = useReservationStore(
    state => state.selectedLocations,
  )
  const setSelectedLocation = useReservationStore(
    state => state.setSelectedLocation,
  )
  const bookForm = useReservationStore(state => state.bookForm)
  const country = useReservationStore(state => state.country)
  const setCountry = useReservationStore(state => state.setCountry)
  const setCity = useReservationStore(state => state.setCity)

  const countryId = country?.id
  const {
    data: airports,
    loading: airportsLoading,
    query: getAirports,
  } = useAirports()
  const {
    data: fromSearchResults,
    loading: fromSearchLoading,
    query: fromSearchLocations,
    reset: resetFromLocations,
  } = useSearchFromLocation()
  const {
    data: toSearchResults,
    loading: toSearchLoading,
    query: toSearchLocations,
    reset: resetToLocations,
  } = useSearchToLocation()

  const { countries, hasMore, isLoading, onLoadMore } = useCountries()

  const fetchNext = () => {
    if (hasMore) onLoadMore()
  }

  const fromPointLat = watch('fromPointLat')
  const toPointLat = watch('toPointLat')
  const fromPointLong = watch('fromPointLong')
  const toPointLong = watch('toPointLong')

  const initialFromLocation = useMemo(
    () => buildAirportFromInitial(initialValues, 'from'),
    [initialValues],
  )
  const initialToLocation = useMemo(
    () => buildAirportFromInitial(initialValues, 'to'),
    [initialValues],
  )

  const fromAirports = useMemo(
    () =>
      mergeAirportOptions({
        options: airports || [],
        searchResults: fromSearchResults,
        selected: selectedLocations.from,
        initial: initialFromLocation,
        compareTo: { lat: toPointLat, long: toPointLong },
      }),
    [
      airports,
      fromSearchResults,
      initialFromLocation,
      selectedLocations.from,
      toPointLat,
      toPointLong,
    ],
  )

  const toAirports = useMemo(
    () =>
      mergeAirportOptions({
        options: airports || [],
        searchResults: toSearchResults,
        selected: selectedLocations.to,
        initial: initialToLocation,
        compareTo: { lat: fromPointLat, long: fromPointLong },
      }),
    [
      airports,
      fromPointLat,
      fromPointLong,
      initialToLocation,
      selectedLocations.to,
      toSearchResults,
    ],
  )

  const debouncedSearchFrom = useDebouncedSearch(
    fromSearchLocations,
    resetFromLocations,
    countryId,
  )
  const debouncedSearchTo = useDebouncedSearch(
    toSearchLocations,
    resetToLocations,
    countryId,
  )

  const onCountryChange = async (countryId: ID | null) => {
    resetFromLocations()
    resetToLocations()
    onLocationChange('from', null, false)
    onLocationChange('to', null, false)
    if (!countryId) return
    const selected = countries.find(
      c => c.id.toString() === countryId.toString(),
    )
    if (selected) setCountry(selected)
    await getAirports({ countryId })
  }

  const onLocationChange = useCallback(
    (field: 'from' | 'to', location: AirportModel | null, validate = true) => {
      setSelectedLocation(field, location)
      const fields = {
        Name: location?.name || location?.address,
        Details: location?.address,
        Lat: location?.latitude,
        Long: location?.longitude,
        PlaceId: location?.place_id,
        Types: location?.types.join(', '),
      }

      Object.entries(fields).forEach(([key, value]) => {
        setValue(`${field}Point${key}`, value, {
          shouldDirty: validate,
          shouldTouch: validate,
          shouldValidate: validate,
        })
      })
      setValue(`${field}Point`, location?.place_id, {
        shouldDirty: validate,
        shouldTouch: validate,
        shouldValidate: validate,
      })
      if (field === 'from' && location?.city) {
        setCity(location?.city)
      }
    },
    [setValue, setCity, setSelectedLocation],
  )

  useEffect(() => {
    if (!initialValues?.countryId) return
    const selected = countries.find(
      c => c.id.toString() === initialValues.countryId?.toString(),
    )
    if (selected) {
      setCountry(selected)
      getAirports({ countryId: initialValues.countryId })
    }
  }, [initialValues?.countryId, countries, getAirports, setCountry])

  useEffect(() => {
    if (!bookForm.fromPointName || !airports?.length) return
    const match = [
      ...(airports || []),
      ...(initialFromLocation ? [initialFromLocation] : []),
    ].find(a => a.name === bookForm.fromPointName)
    if (match) {
      onLocationChange('from', match)
    }
  }, [airports, bookForm.fromPointName, initialFromLocation, onLocationChange])

  useEffect(() => {
    if (!bookForm.toPointName || !airports?.length) return
    const match = [
      ...(airports || []),
      ...(initialToLocation ? [initialToLocation] : []),
    ].find(a => a.name === bookForm.toPointName)
    if (match) {
      onLocationChange('to', match)
    }
  }, [airports, bookForm.toPointName, initialToLocation, onLocationChange])

  return (
    <>
      <Controller
        control={control}
        name='countryId'
        render={({ field: { value, onChange } }) => (
          <Combobox
            value={value?.toString() ?? ''}
            onChange={val => {
              onChange(val)
              onCountryChange(val)
            }}
            onScrollEnd={fetchNext}
            popoverClassName='bg-white'
            isLoading={isLoading}
            disabled={airportsLoading || isLoading}
            placeholder={
              <span className='text-gray-500'>{t('forms.countryPH')}</span>
            }
            items={countries.map(c => ({
              ...c,
              label: c.name,
              value: c.id.toString(),
            }))}
            renderItem={(country, selected) => (
              <CountryOption
                name={country.name}
                flag={country.flag}
                selected={selected}
              />
            )}
            onClear={() => {
              resetFromLocations()
              resetToLocations()
              onChange(null)
              onCountryChange(null)
            }}
          />
        )}
      />

      <div
        className={cn('relative flex items-center rounded-md', inputOutlined)}
      >
        <Separator />
        <div className='w-full'>
          <LocationInput
            field='from'
            airports={fromAirports}
            loading={airportsLoading}
            searching={fromSearchLoading}
            onSelectMap={() => setPickOnMap('from')}
            onLocationChange={onLocationChange}
            onSearch={debouncedSearchFrom}
            onClear={resetFromLocations}
            resetSearch={resetFromLocations}
          />
          <Divider className='mx-auto w-11/12' />
          <LocationInput
            field='to'
            airports={toAirports}
            loading={airportsLoading}
            searching={toSearchLoading}
            onSelectMap={() => setPickOnMap('to')}
            onLocationChange={onLocationChange}
            onSearch={debouncedSearchTo}
            onClear={resetToLocations}
            resetSearch={resetToLocations}
          />
        </div>
      </div>

      <Modal
        size='4xl'
        isOpen={Boolean(pickOnMap)}
        hideCloseButton
        disableBackdropDismiss
        className='xs:p-0 p-3'
      >
        <ModalBody className='p-0'>
          <PickOnMap
            field={pickOnMap}
            onClose={() => setPickOnMap(null)}
            onLocationChange={onLocationChange}
          />
        </ModalBody>
      </Modal>
    </>
  )
}
